import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import { StoryResponse } from '../types/story';
import { useAuth } from '../contexts/AuthContext';
import StoryDisplay from '../components/story/StoryDisplay';
import { logger } from '../utils/logger';
import '../styles/Pages.css';

/**
 * StoryView component for viewing an individual story
 */
const StoryView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [story, setStory] = useState<StoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  // Fetch story when component mounts
  useEffect(() => {
    const fetchStory = async () => {
      if (!id) {
        setError('No story ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // First try to get from session storage (if navigated from My Stories)
        const sessionStory = sessionStorage.getItem('viewStory');
        if (sessionStory) {
          const parsedStory = JSON.parse(sessionStory);
          if (parsedStory.id === id) {
            setStory(parsedStory);
            setLoading(false);
            return;
          }
        }
        
        // Otherwise fetch from API - no need to pass token explicitly now
        const fetchedStory = await ApiService.getStory(id);
        setStory(fetchedStory);
        logger.log('Story loaded:', fetchedStory);
      } catch (err) {
        logger.error('Error fetching story:', err);
        setError(err instanceof Error ? err.message : 'Failed to load your story');
      } finally {
        setLoading(false);
      }
    };

    fetchStory();
  }, [id]);

  // Handle going back to my stories
  const handleBack = () => {
    navigate('/my-stories');
  };
  
  // Handle story update from save/share
  const handleStoryUpdate = (updatedStory: StoryResponse) => {
    setStory(updatedStory);
    logger.log('Story updated:', updatedStory);
  };
  
  // Handle favorite toggle
  const handleFavoriteToggle = (isFavorite: boolean) => {
    if (story) {
      setStory({
        ...story,
        isFavorite
      });
      logger.log(`Story favorite status updated to: ${isFavorite}`);
    }
  };

  // For debugging
  useEffect(() => {
    if (story) {
      logger.log('Story loaded:', {
        id: story.id,
        title: story.title,
        audioUrl: story.audioUrl,
        hasAudio: !!story.audioUrl,
        audioUrlType: typeof story.audioUrl,
        audioUrlStartsWithHttp: story.audioUrl ? story.audioUrl.startsWith('http') : false,
        setting: story.setting,
        mood: story.mood,
        backgroundNoise: story.backgroundNoise
      });
    }
  }, [story]);

  return (
    <div className="page-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-message">Loading Story</div>
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
          <button 
            className="primary-button"
            onClick={handleBack}
          >
            Back to My Stories
          </button>
        </div>
      )}
      
      {!loading && !error && story && (
        <StoryDisplay 
          storyText={story.text}
          audioUrl={story.audioUrl}
          title={story.title}
          onBack={handleBack}
          storyId={story.id}
          onSaveSuccess={handleStoryUpdate}
          isFavorite={story.isFavorite || false}
          onFavoriteToggle={handleFavoriteToggle}
          characters={story.characters}
          hasImages={story.has_images || false}
          imagesStatus={story.images_status || 'none'}
          storyMetadata={{
            backgroundNoise: story.backgroundNoise || story.setting || story.mood || 'calm',
            theme: story.setting || 'forest',
            mood: story.mood || 'calm',
          }}
        />
      )}
    </div>
  );
};

export default StoryView; 